import { useState, useMemo } from "react";
import { Search, Trash2, Beaker, ChevronsUpDown } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { EmptyState } from "./EmptyState";
import { cn } from "@/lib/utils";

export interface IngredientItem {
  ingredientId: string;
  quantityUsed: number;
  variantNamePattern?: string;
}

export interface IngredientOption {
  id: string;
  name: string;
  unit: string;
}

interface IngredientManagerProps {
  ingredients: IngredientItem[];
  availableIngredients: IngredientOption[];
  onChange: (ingredients: IngredientItem[]) => void;
  title?: string;
  description?: string;
  compact?: boolean;
  showVariantPattern?: boolean;
}

export function IngredientManager({
  ingredients = [],
  availableIngredients = [],
  onChange,
  title = "Ingredients",
  description = "Configure which ingredients are used and in what quantity.",
  compact = false,
  showVariantPattern = false,
}: IngredientManagerProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const safeIngredients = Array.isArray(ingredients) ? ingredients : [];
  const safeAvailable = Array.isArray(availableIngredients)
    ? availableIngredients
    : [];

  const filteredIngredients = useMemo(() => {
    if (showVariantPattern) {
      if (!searchTerm) return safeAvailable;

      return safeAvailable.filter((ing) =>
        ing.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    const unusedIngredients = safeAvailable.filter(
      (ing) => !safeIngredients.some((i) => i.ingredientId === ing.id),
    );
    if (!searchTerm) return unusedIngredients;

    return unusedIngredients.filter((ing) =>
      ing.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [safeAvailable, safeIngredients, searchTerm, showVariantPattern]);

  const handleAddIngredient = (ingredientId: string) => {
    if (
      !showVariantPattern &&
      safeIngredients.some((i) => i.ingredientId === ingredientId)
    )

      return;
    onChange([...safeIngredients, { ingredientId, quantityUsed: 1 }]);
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleRemoveIngredient = (index: number) => {
    onChange(safeIngredients.filter((_, i) => i !== index));
  };

  const handleUpdateQuantity = (index: number, quantityUsed: number) => {
    onChange(
      safeIngredients.map((item, i) =>
        i === index ? { ...item, quantityUsed } : item,
      ),
    );
  };

  const handleUpdatePattern = (index: number, variantNamePattern: string) => {
    onChange(
      safeIngredients.map((item, i) =>
        i === index
          ? { ...item, variantNamePattern: variantNamePattern || undefined }
          : item,
      ),
    );
  };

  const getIngredientInfo = (ingredientId: string) => {
    return safeAvailable.find((i) => i.id === ingredientId);
  };

  if (safeAvailable.length === 0) {
    return (
      <div className="space-y-2">
        {title && <Label>{title}</Label>}
        <EmptyState
          icon={Beaker}
          title="No ingredients available"
          description="Create ingredients in the Inventory page first"
        />
      </div>
    );
  }

  const unusedCount = showVariantPattern
    ? safeAvailable.length
    : safeAvailable.length - safeIngredients.length;

  return (
    <div className="space-y-3">
      {title && (
        <div>
          <Label>{title}</Label>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      {safeIngredients.length > 0 && (
        <div
          className={
            compact ? "space-y-2" : "space-y-2 rounded-lg border bg-card p-3"
          }
        >
          {safeIngredients.map((item, index) => {
            const info = getIngredientInfo(item.ingredientId);

            return (
              <div
                key={`${item.ingredientId}-${index}`}
                className={cn(
                  "flex items-center gap-2",
                  showVariantPattern && "flex-wrap",
                )}
              >
                <span
                  className={cn(
                    "text-sm font-medium",
                    showVariantPattern ? "w-full" : "flex-1",
                  )}
                >
                  {info?.name || "Unknown"}
                </span>
                {showVariantPattern && (
                  <Input
                    type="text"
                    placeholder="All variants"
                    className="h-8 flex-1 text-xs"
                    value={item.variantNamePattern ?? ""}
                    onChange={(e) => handleUpdatePattern(index, e.target.value)}
                  />
                )}
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={1}
                    className={cn(
                      "h-8 w-20 text-center",
                      item.quantityUsed < 1 &&
                        "border-destructive focus-visible:ring-destructive",
                    )}
                    value={item.quantityUsed === 0 ? "" : item.quantityUsed}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const parsed = raw === "" ? 0 : parseInt(raw, 10);
                      handleUpdateQuantity(
                        index,
                        Number.isNaN(parsed) ? 0 : parsed,
                      );
                    }}
                    placeholder="1"
                  />
                  <span className="text-xs text-muted-foreground w-8">
                    {info?.unit}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => handleRemoveIngredient(index)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {unusedCount > 0 && (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={isOpen}
              className="w-full justify-between"
            >
              <span className="text-muted-foreground">
                Add ingredient ({unusedCount} available)
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[--radix-popover-trigger-width] p-0"
            align="start"
          >
            <div className="p-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search ingredients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-48 overflow-auto">
              {filteredIngredients.length > 0 ? (
                filteredIngredients.map((ing) => (
                  <button
                    key={ing.id}
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between px-3 py-2 text-sm",
                      "hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer",
                    )}
                    onClick={() => handleAddIngredient(ing.id)}
                  >
                    <span>{ing.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {ing.unit}
                    </span>
                  </button>
                ))
              ) : searchTerm ? (
                <p className="p-3 text-center text-sm text-muted-foreground">
                  No ingredients found.
                </p>
              ) : null}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {safeIngredients.length === 0 && unusedCount > 0 && (
        <p className="text-center text-sm text-muted-foreground py-2">
          No ingredients added yet.
        </p>
      )}
    </div>
  );
}
